// client/src/pages/Search.js — Maxula
// P1-B (v1.154) : page de résultats de recherche avancée.
//   - TOUT l'état des filtres vit dans l'URL (useSearchParams) :
//     partageable, bookmarkable, jamais de localStorage.
//   - Facettes avec compteurs (catégories / villes), fourchette de prix,
//     tri, pagination par boutons (choix volontaire vs scroll infini :
//     plus simple, SEO-friendly, URL stable par page).
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { FiSearch, FiFilter, FiX, FiLoader } from 'react-icons/fi';
import DealCard from '../components/deals/DealCard';
import AdBanner from '../components/common/AdBanner';
import { searchAPI } from '../services/api';
import { useLanguage } from '../i18n/LanguageContext';

const SORT_KEYS = [
  { value: 'newest', key: 'search.sort.newest' },
  { value: 'price_asc', key: 'search.sort.priceAsc' },
  { value: 'price_desc', key: 'search.sort.priceDesc' },
  { value: 'discount_desc', key: 'search.sort.discount' },
  { value: 'popular', key: 'search.sort.popular' }
];

const csv = (v) => String(v || '').split(',').filter(Boolean);

const Search = () => {
  const { t } = useLanguage();
  const [params, setParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [deals, setDeals] = useState([]);
  const [total, setTotal] = useState(0);
  const [, setTotalPages] = useState(1);
  const [facets, setFacets] = useState({ categories: [], cities: [] });
  const [showFilters, setShowFilters] = useState(false); // mobile
  const [priceMin, setPriceMin] = useState(params.get('minPrice') || '');
  const [priceMax, setPriceMax] = useState(params.get('maxPrice') || '');
  // P3-D : scroll infini
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const pageRef = useRef(1);          // page courante chargée (indépendante de l'URL)
  const sentinelRef = useRef(null);

  const q = params.get('q') || '';
  const sort = params.get('sort') || 'newest';
  const selCats = csv(params.get('categoryId'));
  const selCities = csv(params.get('cityId'));

  // Met à jour un paramètre d'URL (et revient page 1 sauf pour "page")
  const setParam = useCallback((key, value) => {
    const next = new URLSearchParams(params);
    if (value === '' || value == null) next.delete(key);
    else next.set(key, String(value));
    if (key !== 'page') next.set('page', '1');
    setParams(next);
  }, [params, setParams]);

  const toggleIn = (key, id) => {
    const cur = csv(params.get(key));
    const sid = String(id);
    const next = cur.includes(sid) ? cur.filter((x) => x !== sid) : [...cur, sid];
    setParam(key, next.join(','));
  };

  const applyPrice = () => {
    const next = new URLSearchParams(params);
    if (priceMin) next.set('minPrice', priceMin); else next.delete('minPrice');
    if (priceMax) next.set('maxPrice', priceMax); else next.delete('maxPrice');
    next.set('page', '1');
    setParams(next);
  };

  const clearAll = () => {
    const next = new URLSearchParams();
    if (q) next.set('q', q);
    setParams(next);
    setPriceMin(''); setPriceMax('');
  };

  const buildQuery = useCallback((pageNum) => ({
    q: q || undefined,
    categoryId: params.get('categoryId') || undefined,
    cityId: params.get('cityId') || undefined,
    minPrice: params.get('minPrice') || undefined,
    maxPrice: params.get('maxPrice') || undefined,
    sort, page: pageNum, limit: 20
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [params]);

  // Chargement initial / à chaque changement de filtre -> on REPART page 1
  useEffect(() => {
    let alive = true;
    setLoading(true);
    pageRef.current = 1;
    searchAPI.search(buildQuery(1))
      .then((r) => {
        if (!alive) return;
        const d = r?.data?.data || {};
        setDeals(d.deals || []);
        setTotal(d.total || 0);
        setTotalPages(d.totalPages || 1);
        setHasMore(!!d.hasMore);
        setFacets(d.facets || { categories: [], cities: [] });
      })
      .catch(() => { if (alive) { setDeals([]); setTotal(0); setHasMore(false); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  // Charger la page suivante et AJOUTER les résultats (scroll infini)
  const loadMore = useCallback(() => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    const nextPage = pageRef.current + 1;
    searchAPI.search(buildQuery(nextPage))
      .then((r) => {
        const d = r?.data?.data || {};
        setDeals((prev) => [...prev, ...(d.deals || [])]);
        setHasMore(!!d.hasMore);
        pageRef.current = nextPage;
      })
      .catch(() => setHasMore(false))
      .finally(() => setLoadingMore(false));
  }, [loadingMore, hasMore, buildQuery]);

  // Observateur d'intersection : déclenche loadMore quand la sentinelle est visible
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore) return undefined;
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) loadMore();
    }, { rootMargin: '400px' });
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, loadMore]);

  const activeFiltersCount = selCats.length + selCities.length
    + (params.get('minPrice') ? 1 : 0) + (params.get('maxPrice') ? 1 : 0);

  const FilterPanel = (
    <div className="space-y-6">
      {/* Tri */}
      <div>
        <h3 className="font-semibold text-sm text-gray-900 mb-2">{t('search.sortBy')}</h3>
        <select value={sort} onChange={(e) => setParam('sort', e.target.value)}
          className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200">
          {SORT_KEYS.map((s) => <option key={s.value} value={s.value}>{t(s.key)}</option>)}
        </select>
      </div>

      {/* Prix */}
      <div>
        <h3 className="font-semibold text-sm text-gray-900 mb-2">{t('search.price')}</h3>
        <div className="flex items-center gap-2">
          <input type="number" min="0" placeholder={t('search.min')} value={priceMin}
            onChange={(e) => setPriceMin(e.target.value)}
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200" />
          <span className="text-gray-400">—</span>
          <input type="number" min="0" placeholder={t('search.max')} value={priceMax}
            onChange={(e) => setPriceMax(e.target.value)}
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200" />
        </div>
        <button type="button" onClick={applyPrice}
          className="mt-2 w-full rounded-lg bg-gray-900 text-white text-sm font-semibold py-2 hover:bg-gray-800">
          {t('common.apply')}
        </button>
      </div>

      {/* Catégories */}
      {facets.categories.length > 0 && (
        <div>
          <h3 className="font-semibold text-sm text-gray-900 mb-2">{t('search.categories')}</h3>
          <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
            {facets.categories.map((c) => (
              <label key={c.id} className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" className="h-4 w-4 accent-pink-600"
                  checked={selCats.includes(String(c.id))}
                  onChange={() => toggleIn('categoryId', c.id)} />
                <span className="flex-1 text-gray-700">{c.name}</span>
                <span className="text-xs text-gray-400">({c.count})</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Villes */}
      {facets.cities.length > 0 && (
        <div>
          <h3 className="font-semibold text-sm text-gray-900 mb-2">{t('search.cities')}</h3>
          <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
            {facets.cities.map((c) => (
              <label key={c.id} className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" className="h-4 w-4 accent-pink-600"
                  checked={selCities.includes(String(c.id))}
                  onChange={() => toggleIn('cityId', c.id)} />
                <span className="flex-1 text-gray-700">{c.name}</span>
                <span className="text-xs text-gray-400">({c.count})</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {activeFiltersCount > 0 && (
        <button type="button" onClick={clearAll}
          className="w-full inline-flex items-center justify-center gap-1.5 rounded-lg border border-gray-200 text-sm text-gray-600 py-2 hover:bg-gray-50">
          <FiX size={14} /> {t('common.reset')}
        </button>
      )}
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* En-tête résultats */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FiSearch className="text-primary-600" />
            {q ? t('search.resultsFor', { q }) : t('search.allDeals')}
          </h1>
          {!loading && (
            <p className="text-sm text-gray-500 mt-1">
              {t(total > 1 ? 'search.results_other' : 'search.results_one', { count: total })}
              {activeFiltersCount > 0 ? ` · ${t(activeFiltersCount > 1 ? 'search.activeFilters_other' : 'search.activeFilters_one', { count: activeFiltersCount })}` : ''}
            </p>
          )}
        </div>
        <button type="button" onClick={() => setShowFilters((v) => !v)}
          className="lg:hidden inline-flex items-center gap-2 rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium">
          <FiFilter size={15} /> {t('search.filters')}{activeFiltersCount > 0 ? ` (${activeFiltersCount})` : ''}
        </button>
      </div>

      {/* Publicité — haut des résultats */}
      <div className="mb-6">
        <AdBanner placement="deals_top" />
      </div>

      <div className="grid lg:grid-cols-4 gap-8">
        {/* Filtres : sidebar desktop / panneau mobile */}
        <aside className={`lg:col-span-1 ${showFilters ? 'block' : 'hidden'} lg:block`}>
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 lg:sticky lg:top-24">
            {FilterPanel}
          </div>
          {/* Publicité — barre latérale (verticale) */}
          <div className="mt-6 hidden lg:block">
            <AdBanner placement="sidebar" orientation="vertical" />
          </div>
        </aside>

        {/* Résultats */}
        <main className="lg:col-span-3">
          {loading ? (
            <div className="flex items-center justify-center py-24 text-gray-400">
              <FiLoader size={28} className="animate-spin" />
            </div>
          ) : deals.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-10 text-center">
              <FiSearch size={36} className="mx-auto text-gray-300 mb-3" />
              <h2 className="font-semibold text-gray-900 mb-1">{t('search.noResults')}</h2>
              <p className="text-sm text-gray-500 mb-5">
                {t('search.noResultsHint')}
              </p>
              {facets.categories.length > 0 && (
                <div className="flex flex-wrap justify-center gap-2">
                  {facets.categories.slice(0, 6).map((c) => (
                    <button key={c.id} type="button" onClick={() => setParam('categoryId', String(c.id))}
                      className="px-3 py-1.5 rounded-full bg-primary-50 text-primary-700 text-sm font-medium hover:bg-primary-100">
                      {c.name} ({c.count})
                    </button>
                  ))}
                </div>
              )}
              <div className="mt-6">
                <Link to="/deals" className="text-primary-600 text-sm font-semibold hover:underline">
                  {t('search.viewAllDeals')}
                </Link>
              </div>
            </div>
          ) : (
            <>
              <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5">
                {deals.map((deal) => (
                  <DealCard key={deal._id ?? deal.id} deal={deal} />
                ))}
              </div>

              {/* P3-D : scroll infini — sentinelle + repli bouton */}
              {hasMore && (
                <div ref={sentinelRef} className="mt-8 flex items-center justify-center">
                  <button type="button" onClick={loadMore} disabled={loadingMore}
                    className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-5 py-2.5 text-sm font-medium disabled:opacity-50 hover:bg-gray-50">
                    {loadingMore ? <><FiLoader className="animate-spin" size={15} /> {t('common.loading')}</> : t('common.seeMore')}
                  </button>
                </div>
              )}
              {!hasMore && deals.length > 0 && (
                <p className="mt-8 text-center text-sm text-gray-400">
                  {t(total > 1 ? 'search.results_other' : 'search.results_one', { count: total })}
                </p>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
};

export default Search;
